<template>
  <div class="map-wrapper">
    <div ref="mapContainer" class="map-container"></div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fetchZoneGeoJson } from "@/api/zones";
import { createMarker } from "@/utils/markerFactory";

const props = defineProps({
  center: {
    type: Array,
    default: () => [53.41291, -8.24389], // Center of Ireland
  },
  zoom: {
    type: Number,
    default: 10,
  },
  geojson: {
    type: Object,
    default: null,
  },
  marker: {
    type: Array,
    default: null,
  },
  markerData: {
    type: Object,
    default: null,
  },
  zoneId: {
    type: String,
    default: null,
  },
  height: {
    type: String,
    default: "400px",
  },
  showZoomControls: {
    type: Boolean,
    default: true,
  },
  allowLocationSelection: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["location-selected"]);

const mapContainer = ref(null);
let map = null;
let geojsonLayer = null;
let markerLayer = null;
let clickMarker = null;
let geometryLoaded = false; // Flag to track if geometry has been loaded

onMounted(() => {
  // Initialize map
  map = L.map(mapContainer.value, {
    center: props.center,
    zoom: props.zoom,
    zoomControl: props.showZoomControls,
    attributionControl: false,
    // ZoneMapSimple is used as an informational preview inside scrollable
    // card lists (Zones grid, etc.). Mouse-wheel zoom would hijack the page
    // scroll — kill it. Drag/click still work for inspection.
    scrollWheelZoom: false,
  });

  // Base layer
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  // Load zone geojson if zoneId provided
  if (props.zoneId) {
    loadZoneGeojson(props.zoneId);
  } else if (props.geojson) {
    // Add GeoJSON if provided directly
    loadGeojson(props.geojson);
  }

  // Add marker if given
  if (props.marker && props.marker.length === 2) {
    addOrUpdateMarker(props.marker);
  }

  // Add click handler for location selection
  if (props.allowLocationSelection) {
    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (clickMarker) {
        clickMarker.setLatLng(e.latlng);
      } else {
        clickMarker = L.marker(e.latlng, {
          icon: L.icon({
            iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
            shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          }),
        }).addTo(map);
      }
      emit("location-selected", lat, lng);
    });
  }
});

// Watch zoneId changes
watch(
  () => props.zoneId,
  async (newZoneId) => {
    if (!map) return;
    if (newZoneId) {
      await loadZoneGeojson(newZoneId);
    } else {
      if (geojsonLayer) {
        geojsonLayer.removeFrom(map);
        geojsonLayer = null;
      }
    }
  }
);

// Watch geojson changes
watch(
  () => props.geojson,
  (newGeo) => {
    if (!map) return;
    loadGeojson(newGeo);
  }
);

// Watch center changes: move map center if changed (but only if no geometry is loaded)
watch(
  () => props.center,
  (newCenter) => {
    if (!map || !newCenter) return;
    // Don't update map center if geometry has been loaded (let the geometry control the view)
    if (geometryLoaded || geojsonLayer) return;
    map.setView(newCenter, props.zoom);
  }
);

// Watch marker changes: add/update marker and center map on it
watch(
  () => props.marker,
  (newMarker) => {
    if (!map) return;
    if (newMarker && newMarker.length === 2) {
      addOrUpdateMarker(newMarker);
      map.setView(newMarker, props.zoom);
    } else if (markerLayer) {
      // Remove marker if null
      map.removeLayer(markerLayer);
      markerLayer = null;
    }
  }
);

// Re-render the marker when its visual data (iconName/iconColour) changes —
// e.g. when the parent finally resolves the form template and passes it in.
// Without this, the marker stays as whatever was first built and the late-
// arriving form icon never gets applied.
watch(
  () => props.markerData,
  () => {
    if (!map) return;
    if (props.marker && props.marker.length === 2) {
      addOrUpdateMarker(props.marker);
    }
  },
  { deep: true }
);

onBeforeUnmount(() => {
  if (map) map.remove();
  map = null;
});

function buildFeaturePopup(props) {
  if (!props) return "";
  const esc = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

  const name = props.name || props.Name || props.title || props.OverlayName || "";
  const rows = [];
  const add = (label, val) => {
    if (val !== undefined && val !== null && String(val).trim() !== "")
      rows.push(`<div><strong>${label}:</strong> ${esc(val)}</div>`);
  };
  add("Category", props.category);
  add("Type", props.wwCategory);
  add("Source", props.source);
  add("Ref", props.externalId);

  if (!name && rows.length === 0) return "";
  const title = name ? `<div style="font-weight:600;margin-bottom:3px;">${esc(name)}</div>` : "";
  return `<div style="min-width:150px;font-size:12px;line-height:1.4;">${title}${rows.join("")}</div>`;
}

function loadGeojson(geojson) {
  if (geojsonLayer) {
    geojsonLayer.removeFrom(map);
    geojsonLayer = null;
  }
  if (!geojson) {
    geometryLoaded = false;
    return;
  }

  geojsonLayer = L.geoJSON(geojson, {
    style: (feature) => ({
      color: feature.properties?.colour || "#ff0000",
      weight: 3,
      fillOpacity: 0.2,
    }),
    pointToLayer: (feature, latlng) =>
      L.circleMarker(latlng, {
        radius: 5,
        fillColor: feature.properties?.colour || "#ff0000",
        color: "#000",
        weight: 1,
      }),
    onEachFeature: (feature, layer) => {
      const html = buildFeaturePopup(feature?.properties);
      if (html) layer.bindPopup(html);
    },
  }).addTo(map);

  // Mark geometry as loaded BEFORE fitting bounds
  geometryLoaded = true;

  // Fit bounds to geometry (this should take priority over center prop)
  const bounds = geojsonLayer.getBounds();
  if (bounds.isValid()) {
    map.fitBounds(bounds, { padding: [20, 20] });
  }
}

function addOrUpdateMarker(latlng) {
  if (markerLayer) {
    map.removeLayer(markerLayer);
    markerLayer = null;
  }

  const [lat, lng] = latlng;
  if (props.markerData) {
    const { marker } = createMarker(
      {
        type: "finding",
        lat,
        lng,
        ...props.markerData,
      },
      {},
      { markerOptions: { draggable: false } }
    );
    markerLayer = marker.addTo(map);
  } else {
    markerLayer = L.marker(latlng).addTo(map);
  }
}

async function loadZoneGeojson(zoneId) {
  try {
    const geojson = await fetchZoneGeoJson(zoneId);
    if (geojson) {
      loadGeojson(geojson);
    }
  } catch (err) {
    console.error("Failed to load zone geojson:", err);
  }
}
</script>

<style scoped>
.map-wrapper {
  width: 100%;
  height: 100%;
}
.map-container {
  width: 100%;
  height: v-bind(height);
  border-radius: 10px;
  box-shadow: 5px 7px 5px -3px rgba(186, 186, 186, 0.75);
  -webkit-box-shadow: 5px 7px 5px -3px rgba(186, 186, 186, 0.75);
  -moz-box-shadow: 5px 7px 5px -3px rgba(186, 186, 186, 0.75);
}

:deep(.leaflet-div-icon) {
  background: transparent !important;
  border: none !important;
}

:deep(.wm .wm-circle) {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid transparent;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  position: relative;
}

:deep(.wm .wm-fa) {
  color: #fff;
  font-size: 16px;
  line-height: 16px;
}

:deep(.wm .wm-img) {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
  background: transparent;
}

:deep(.wm .wm-badge) {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #111;
  color: #fff;
  border-radius: 10px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 16px;
  height: 16px;
  min-width: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}
</style>
